/**
 * announceForWeb.ts
 *
 * Directly injects two visually-hidden `aria-live` regions into
 * `document.body` and alternates writes between them to announce toast
 * messages to screen readers on web.
 *
 * Why two alternating regions instead of one clear-then-set region?
 * Writing into a *new* text-content mutation on an already-mounted node is
 * the one thing screen readers reliably pick up across Chrome, Safari, and
 * NVDA. A single region that gets cleared then re-set on a timer is prone to
 * race conditions (the AT can miss the update if it polls between the clear
 * and the set, or coalesces the two mutations). Alternating between two
 * permanently-mounted nodes means every announcement is always a fresh
 * "this previously-empty/stale node just got new text" mutation on a node
 * the AT has already indexed — no clearing race, no same-value no-op.
 * This is the same double-buffer approach used by @react-aria/live-announcer.
 *
 * Why not use RNW's AccessibilityInfo.announceForAccessibility()?
 * React Native Web's internal aria-live polyfill is unreliable in Safari +
 * VoiceOver and does not work at all in some NVDA + Firefox combinations.
 * Direct DOM manipulation is the only fully cross-browser reliable approach.
 *
 * WCAG 2.1 AA: SC 4.1.3 Status Messages
 */

const REGION_COUNT = 2;
const LIVE_REGION_ID_PREFIX = '__rn_toast_live_region__';

let regions: HTMLElement[] | null = null;
let nextRegionIndex = 0;
let announceTimer: ReturnType<typeof setTimeout> | null = null;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

function createLiveRegion(id: string): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('id', id);
  // role="alert" implies aria-live="assertive" cleanly (no conflicting
  // implicit/explicit live-ness like role="status" + aria-live="assertive" would give).
  el.setAttribute('role', 'alert');
  el.setAttribute('aria-live', 'assertive');
  el.setAttribute('aria-atomic', 'true');
  el.setAttribute('aria-relevant', 'additions text');
  // Off-screen clip pattern — keeps element in AX tree (unlike display:none / visibility:hidden)
  el.style.cssText =
    'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;clip:rect(1px,1px,1px,1px);white-space:nowrap;';
  document.body.appendChild(el);
  return el;
}

function getOrCreateLiveRegions(): HTMLElement[] | null {
  if (typeof document === 'undefined') return null;

  if (regions) return regions;

  regions = [];
  for (let i = 0; i < REGION_COUNT; i++) {
    const id = `${LIVE_REGION_ID_PREFIX}${i}`;
    regions.push(document.getElementById(id) ?? createLiveRegion(id));
  }
  return regions;
}

/**
 * Announce `message` to screen readers via a DOM aria-live region.
 * Alternates between two permanently-mounted regions so every call is a
 * text-content mutation on an already-present node — the reliable case for
 * AT announcement across Chrome, Safari, and NVDA.
 *
 * @param message - The text to announce.
 * @param clearAfterMs - How long (ms) before clearing the active region (default 7 s).
 */
export function announceToScreenReader(
  message: string,
  clearAfterMs = 7000,
): void {
  if (typeof document === 'undefined') return;

  const liveRegions = getOrCreateLiveRegions();
  if (!liveRegions) return;

  if (announceTimer !== null) clearTimeout(announceTimer);
  if (clearTimer !== null) clearTimeout(clearTimer);

  const target = liveRegions[nextRegionIndex];
  nextRegionIndex = (nextRegionIndex + 1) % liveRegions.length;
  if (!target) return;

  // Chrome's Blink accessibility bridge (unlike Safari/WebKit) can miss a
  // live-region text mutation that lands in the same tick as a larger
  // surrounding DOM change — e.g. the toast view itself mounting. Deferring
  // past the current paint via rAF, then a short macrotask delay, gives
  // Chrome's accessibility tree an isolated mutation to pick up.
  requestAnimationFrame(() => {
    announceTimer = setTimeout(() => {
      target.textContent = message;

      clearTimer = setTimeout(() => {
        target.textContent = '';
      }, clearAfterMs);
    }, 100);
  });
}
